import { Box, Button, Divider, FormControlLabel, Grid, Switch, TextField } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import React, { useState } from "react"; 
import { useLocation } from 'react-router-dom';
import { useAuth, useDOUploadFile } from "hooks";
import LoadingWrapper from "components/Shared/LoadingWrapper";
import InnerShadowBox from "components/Shared/InnerShadowBox";
import { BaseTextField } from "components/FormBuilder/fields/BaseTextField";
import FileDropzone from "components/Shared/FileDropzone";
import { useListListQuery } from "store/services/listService";
import { ENTITY_LIST_CONSTANTS } from "data/static";
import BaseSelect from "components/Shared/Base/BaseSelect";

const INITIAL_VALUE = {
  title: "",
  attachments: [],
  isActive: "",
  vendorDocumentType: "",
  documentType: "",
  expiryDate: "",
  hasExpiry: false, 
};

const DocumentForm = ({
  isBusy = false,
  editDocumentData,
  onSubmit = () => { },
  onCancel = () => { },
}) => {
  const location = useLocation();
  const [showExpiry, setShowExpiry] = useState(false); 

  // Check if URL contains 'vendor-profile'
  const isVendorProfile = location.pathname.includes('vendor-profile');

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    defaultValues: INITIAL_VALUE,
  });

  const hasExpiry = watch("hasExpiry"); 

  const { data: documentTypeList = [], isLoading: loadingDocumentTypes } =
    useListListQuery({
      type: isVendorProfile
        ? ENTITY_LIST_CONSTANTS.VENDOR_DOCUMENT_TYPES
        : ENTITY_LIST_CONSTANTS.DOCUMENT_TYPES,
      status: "Active",
    });

  const _mappedDocumentTypes = React.useMemo(
    () =>
      Array.isArray(documentTypeList)
        ? documentTypeList.map((item) => ({
          label: item?.name,
          value: item?._id,
        }))
        : [],
    [documentTypeList]
  );

  const document = editDocumentData;

  const { handleUploadFiles, uploadingFiles } = useDOUploadFile();
  const { viewEntity } = useAuth();

  React.useEffect(() => {
    if (!document) return;

    const hasExpiryValue = !!document?.expiryDate;
    
    reset({
      title: document?.title,
      isActive: document?.isActive,
      expiryDate: document?.expiryDate ? document.expiryDate.split('T')[0] : "", 
      vendorDocumentType: document?.vendorDocumentType?._id || null,
      documentType: document?.documentType?._id || null,
      attachments: document?.attachments || [],
      hasExpiry: hasExpiryValue, 
    });
    
    setShowExpiry(hasExpiryValue); // Set showExpiry state
  }, [reset, document]);

  const handleSubmitOrderDocument = async (data) => {
    const payload = {
      ...data,
      // If hasExpiry is false, set expiryDate to null
      expiryDate: data.hasExpiry ? data.expiryDate : null,
      vendorDocumentType: data.vendorDocumentType || null,
      documentType: data.documentType || null,
    };

    const [existingAttachments, newAttachments] = data?.attachments?.reduce(
      ([pass, fail], el) =>
        el?.publicId || el?.url ? [[...pass, el], fail] : [pass, [...fail, el]],
      [[], []]
    );

    if (newAttachments?.length > 0) {
      try {
        const { uploadedFiles } = await handleUploadFiles(newAttachments, {
          folderName: "Document",
          entity: viewEntity?.entity?._id,
        });

        payload.attachments = [...existingAttachments, ...uploadedFiles];
      } catch (error) {
        return;
      }
    } else {
      payload.attachments = existingAttachments;
    }

    onSubmit(payload);
  };

  return (
    <LoadingWrapper loading={isBusy || uploadingFiles}>
      <InnerShadowBox padding={3}>
        <Grid container spacing={5}>
          <Grid item md={12}>
            <Controller
              name="title"
              control={control}
              rules={{ required: "Title is required" }}
              render={({ field }) => (
                <BaseTextField
                  fullWidth
                  label="Title"
                  type="text"
                  size="small"
                  {...field}
                  onChange={(e) => setValue("title", e.target.value)}
                />
              )}
            />
            {errors.title && <p className="error">{errors.title.message}</p>}
          </Grid>

          <Grid item xs={12}>
            <Controller
              name={isVendorProfile ? "vendorDocumentType" : "documentType"}
              control={control}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  fullWidth
                  size="small"
                  label="Document Type"
                  options={_mappedDocumentTypes}
                  getOptionLabel={(opt) => opt.label || ""}
                  getOptionValue={(opt) => opt.value || ""}
                  error={!!errors.vendorDocumentType || !!errors.documentType}
                  helperText={errors?.vendorDocumentType?.message || errors?.documentType?.message}
                  loading={loadingDocumentTypes}
                  loadingText="Loading Document Types..."
                />
              )}
            />
          </Grid>

          {/* Show checkbox only for vendor profile */}
          {isVendorProfile && (
            <Grid item xs={12}>
              <Controller
                name="hasExpiry"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value || false}
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          setShowExpiry(e.target.checked);
                        }}
                      />
                    }
                    label="Has Expiry Date?"
                  />
                )}
              />
            </Grid>
          )}

          {/* Show expiry date field only when checkbox is checked or when editing a document with expiry date */}
          {(showExpiry || (isVendorProfile && hasExpiry)) && (
            <Grid item xs={12}>
              <Controller
                name="expiryDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    fullWidth
                    label="Expiry Date"
                    type="date"
                    {...field}
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.expiryDate}
                    helperText={errors.expiryDate?.message}
                    disabled={!hasExpiry}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Controller
              name="attachments"
              control={control}
              render={({ field }) => (
                <FileDropzone
                  files={field.value || []}
                  onFileChange={(files) => setValue("attachments", files)}
                  multiple={true}
                  maxFiles={5}
                />
              )}
            />
          </Grid>

          {editDocumentData && (<Grid item md={6}>
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value || false}
                      {...field}
                    />
                  }
                  label="Is Active?"
                />
              )}
            />
          </Grid>)}

        </Grid>
      </InnerShadowBox>

      <Divider />

      <Box
        display="flex"
        justifyContent="flex-end"
        gap={2}
        paddingX={3}
        paddingY={2}
      >
        <Button disabled={isBusy} onClick={onCancel}>
          Cancel
        </Button>

        <Button
          variant="outlined"
          disabled={isBusy}
          onClick={handleSubmit(handleSubmitOrderDocument)}
        >
          {document ? "Update" : "Add"}
        </Button>
      </Box>
    </LoadingWrapper>
  );
};

export default DocumentForm;